// src/constants/index.ts

import { BlockchainNetwork } from '../types';

// ========== Application Constants ==========
export const APP_NAME = 'Crypto Top Signals Dashboard';
export const APP_VERSION = '1.0.0';

// ========== API Endpoints ==========

// Base API URLs
export const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://api.your-dashboard-domain.com' 
  : 'http://localhost:3001';

// CoinGecko API - use direct calls since CORS is allowed for free tier
export const COINGECKO_API_BASE = 'https://api.coingecko.com/api/v3';
export const COINGECKO_PRO_API_BASE = process.env.NODE_ENV === 'development'
  ? '/api/coingecko'
  : 'https://pro-api.coingecko.com/api/v3';

// Alternative.me API (Fear & Greed Index)
export const FEAR_GREED_API = 'https://api.alternative.me/fng/';

// News APIs
export const NEWS_API_BASE = 'https://newsapi.org/v2';
export const GNEWS_API_BASE = 'https://gnews.io/api/v4';
export const MEDIASTACK_API_BASE = 'http://api.mediastack.com/v1';

// Google Trends API (via unofficial API or proxy)
export const GOOGLE_TRENDS_API = `${API_BASE_URL}/api/trends`;

// Blockchain APIs
export const ETHERSCAN_API_BASE = 'https://api.etherscan.io/api';
export const BASESCAN_API_BASE = 'https://api.basescan.org/api';
export const SOLSCAN_API_BASE = 'https://api.solscan.io';

// RPC URLs
export const ETH_RPC_URL = 'https://eth-mainnet.g.alchemy.com/v2';
export const BASE_RPC_URL = 'https://mainnet.base.org';
export const SOLANA_RPC_URL = 'https://api.mainnet-beta.solana.com';

// Beaconchain API
export const BEACONCHAIN_API_BASE = 'https://beaconcha.in/api/v1';

// Exchange APIs
export const BINANCE_API_BASE = 'https://api.binance.com/api/v3';
export const COINBASE_API_BASE = 'https://api.exchange.coinbase.com';
export const BYBIT_API_BASE = 'https://api.bybit.com';
export const HYPERLIQUID_API_BASE = 'https://api.hyperliquid.xyz';
export const DYDX_API_BASE = 'https://api.dydx.exchange';

// ========== Data Refresh Intervals (in milliseconds) ==========
export const REFRESH_INTERVALS = {
  MARKET_DATA: 5 * 60 * 1000, // 5 minutes
  FEAR_GREED: 60 * 60 * 1000, // 1 hour
  ONCHAIN_DATA: 10 * 60 * 1000, // 10 minutes
  WALLET_DATA: 15 * 60 * 1000, // 15 minutes
  NEWS_DATA: 30 * 60 * 1000, // 30 minutes
  GOOGLE_TRENDS: 60 * 60 * 1000, // 1 hour
  BEACONCHAIN: 5 * 60 * 1000, // 5 minutes
  OPEN_INTEREST: 5 * 60 * 1000, // 5 minutes
};

// ========== Target Values ==========
export const TARGET_MARKET_CAP = 7_000_000_000_000; // 7 trillion USD
export const CURRENT_MARKET_CAP_MILESTONE = 3_000_000_000_000; // 3 trillion USD

// ========== Threshold Values ==========
export const THRESHOLDS = {
  // RSI Thresholds
  RSI_OVERBOUGHT: 70,
  RSI_OVERSOLD: 30,
  
  // Fear & Greed Index Classifications
  FEAR_GREED: {
    EXTREME_FEAR: 25,
    FEAR: 40,
    NEUTRAL: 55,
    GREED: 75,
    EXTREME_GREED: 100,
  },
  
  // Market Heat Score Classifications
  MARKET_HEAT: {
    COLD: 20,
    COOL: 40,
    NEUTRAL: 60,
    WARM: 75,
    HOT: 90,
    OVERHEATED: 100,
  },
  
  // Wallet Tracking
  WALLET: {
    MIN_AGE_DAYS: 180, // 6 months minimum holding period
    MIN_BALANCE_USD: 100000, // $100k minimum balance
    WHALE_THRESHOLD_USD: 1000000, // $1M+ considered a whale
    SIGNIFICANT_MOVEMENT_PCT: 10, // 10% movement is significant
  },
};

// ========== Weights for Market Heat Score ==========
export const MARKET_HEAT_WEIGHTS = {
  FEAR_GREED_INDEX: 0.15, // 15% (reduced from 20%)
  MARKET_CAP_PROXIMITY: 0.10, // 10% (reduced from 15%)
  GOOGLE_TRENDS: 0.10, // 10%
  RSI: 0.15, // 15%
  UNSTAKING_QUEUE: 0.15, // 15% (increased from 5%)
  OPEN_INTEREST: 0.15, // 15%
  NEWS_ACTIVITY: 0.10, // 10%
  WALLET_ACTIVITY: 0.10, // 10%
};

// ========== Assets to Track ==========
export const TRACKED_ASSETS = {
  ETHEREUM: {
    id: 'ethereum',
    symbol: 'ETH',
    name: 'Ethereum',
  },
  SPX6900: {
    id: 'spx6900',
    symbol: 'SPX6900',
    name: 'SPX6900',
  },
  BITCOIN: {
    id: 'bitcoin',
    symbol: 'BTC',
    name: 'Bitcoin',
  },
};

// ========== News Sources to Monitor ==========
export const NEWS_SOURCES = [
  'CNN',
  'NBC',
  'CNBC',
  'FOX',
  'SNL',
  'Bloomberg',
  'Reuters',
  'WSJ',
];

// ========== SPX6900 Token Contract Addresses ==========
export const SPX6900_TOKEN_CONTRACTS = {
  ETHEREUM: {
    address: '0xe0f63a424a4439cbe457d80e4f4b51ad25b2c56c',
    decimals: 8,
    symbol: 'SPX6900'
  },
  BASE: {
    address: '0x50da645f148798f68ef2d7db7c1cb22a6819bb2c',
    decimals: 8,
    symbol: 'SPX6900'
  },
  SOLANA: {
    address: 'J3NKxxXZcnNiMjKw9hYb2K4LUxgwB6t1FtPtQVsv3KFr',
    decimals: 8, // Fixed: SPX6900 on Solana has 8 decimals, not 9
    symbol: 'SPX6900'
  }
};

// ========== Whale Wallet Addresses for SPX6900 ==========
export const SPX6900_WHALE_ADDRESSES = {
  ETHEREUM: [
    // Add Ethereum addresses here
    '0x86cbdba59f9317d002d03aa6f24f81effc731c4a',
    '0xaf06ff6243d166d20438d31b294b421c19994899',
    '0xaf6dbb5db25a444c085908f71589b3f121c841ef',
    '0x6b411100c72ba2445e50ffd20839c28b3546de7c',
    '0x85dead62a85cff13fdbe8dc1005f4593f578598b',
    '0x13FC38Ec99A8217A06D1dC6DB8c0bF0Ee97EBF7f',
  ],
  BASE: [
    // Updated addresses - removed transfer wallets, added new tracking addresses
    '0x493210103ace77acd645dfb0c616d20cab83e947',
    '0x66e4e30cf1eb6155c1bf0422879a470e582f3a50',
    '0xe0f693f4dc486fbe734e3afff5fe03fba08a64f3',
    '0x7C005131eDCE9c25CfB2532DBf5869F644eAaD7F',
    '0x444C5D6994b96Fb9acb9Fa71017cBE7c54c68551',
    '0x362356E57638d8d9a623693b141e7E7C9E5002A4',
  ],
  SOLANA: [
    // Add Solana addresses here
    'BwgJCgLPrRJZCANgj9PKqt67Jn4y3CkFbFJRwLZecJ9',
    '6coezjVVwA2u53ByyZkiNXkn88eNx9AeHpS3B3oiNccm',
    '3RieTyY7uM1KducnoBgjPvc1kpna94DSrCCaxz5a1ns6',
    'FADpXThkp23CaJJZ9zheG6f8Bs4ZST5doSTBhqDaUBrt',
    'DMe3ddj7awSR3LFC64rjmCPexsrSv33QAxFoJux4vGH3',
    '8ajcFWdN22eT2kPQajiWZbimrYF8amdGCnWHjVjBPtyq',
    '4eCsDGPRL7yEBNkUBzAdB88HuT91fAvL3zdbsHZN4yn4',
    '2KGHD5uC1K8czvpdaZ1b3HWzvYEKFv45KK2EYC4gQCq9',
  ],
};

// ========== Blockchain Networks ==========
export const BLOCKCHAIN_NETWORKS: BlockchainNetwork[] = [
  'Ethereum',
  'Base',
  'Solana',
];

// ========== Chart Timeframes ==========
export const CHART_TIMEFRAMES = [
  { label: '1D', value: '1d' },
  { label: '1W', value: '1w' },
  { label: '1M', value: '1m' },
  { label: '3M', value: '3m' },
  { label: '6M', value: '6m' },
  { label: '1Y', value: '1y' },
  { label: 'All', value: 'all' },
];

// ========== UI Constants ==========
export const UI = {
  ANIMATIONS: {
    TRANSITION_DURATION: 300, // ms
    CHART_ANIMATION_DURATION: 800, // ms
    DATA_REFRESH_ANIMATION_DURATION: 500, // ms
  },
  COLORS: {
    POSITIVE: '#16C784',
    NEGATIVE: '#EA3943',
    NEUTRAL: '#8A92B2',
    ETHEREUM: '#627EEA',
    SPX6900: '#F7931A', // Using Bitcoin orange as placeholder
    BITCOIN: '#F7931A',
    FEAR_GRADIENT: [
      '#EA3943', // Extreme Fear
      '#FF6B6B', // Fear
      '#FFD700', // Neutral
      '#90EE90', // Greed
      '#008000', // Extreme Greed
    ],
    CHART_GRADIENT: {
      POSITIVE: ['rgba(22, 199, 132, 0.2)', 'rgba(22, 199, 132, 0)'],
      NEGATIVE: ['rgba(234, 57, 67, 0.2)', 'rgba(234, 57, 67, 0)'],
    },
  },
  GRID_BREAKPOINTS: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
  },
};

// ========== Market Heat History Settings ==========
export const MARKET_HEAT_HISTORY = {
  CACHE_KEY: 'crypto:market_heat_history',
  RETENTION_DAYS: 365 * 5, // 5 years - effectively unlimited for practical purposes
  MIN_PERSIST_INTERVAL_MS: 30 * 60 * 1000, // 30 minutes
  MIN_SCORE_CHANGE: 2, // Minimum score change to persist a new entry
};

// ========== Default Dashboard Settings ==========
export const DEFAULT_DASHBOARD_SETTINGS = {
  refreshRates: {
    marketData: REFRESH_INTERVALS.MARKET_DATA,
    newsData: REFRESH_INTERVALS.NEWS_DATA,
    walletData: REFRESH_INTERVALS.WALLET_DATA,
  },
  displayOptions: {
    theme: 'dark' as const,
    layout: 'grid' as const,
    density: 'comfortable' as const,
    chartTimeframe: '1w' as const,
  },
  alertThresholds: {
    marketHeat: THRESHOLDS.MARKET_HEAT.HOT,
    rsiOverbought: THRESHOLDS.RSI_OVERBOUGHT,
    rsiOversold: THRESHOLDS.RSI_OVERSOLD,
    significantWalletMovement: THRESHOLDS.WALLET.SIGNIFICANT_MOVEMENT_PCT,
  },
  walletTracking: {
    minAgeInDays: THRESHOLDS.WALLET.MIN_AGE_DAYS,
    minBalanceUSD: THRESHOLDS.WALLET.MIN_BALANCE_USD,
    trackedNetworks: BLOCKCHAIN_NETWORKS,
  },
};
